import { api } from "../lib/api";

/**
 * Avisos al móvil del socio.
 *
 * El navegador se suscribe al servicio de notificaciones de su fabricante
 * (Google o Apple) y nos devuelve una dirección; el club envía los avisos a
 * traves de ese servicio. Nosotros solo guardamos esa dirección.
 */

export type EstadoAvisos = "no-soportado" | "requiere-instalar" | "sin-activar" | "activados" | "bloqueados";

/** En iPhone las notificaciones solo funcionan si el portal está en la pantalla de inicio. */
function esIosSinInstalar(): boolean {
  const esIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const instalado = window.matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
  return esIos && !instalado;
}

export function soporta(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function estadoActual(): Promise<EstadoAvisos> {
  if (!soporta()) return esIosSinInstalar() ? "requiere-instalar" : "no-soportado";
  if (Notification.permission === "denied") return "bloqueados";

  const registro = await navigator.serviceWorker.getRegistration();
  const suscripcion = await registro?.pushManager.getSubscription();
  return suscripcion ? "activados" : "sin-activar";
}

/** El navegador quiere la clave pública en bytes, no en texto. */
function aBytes(base64: string): Uint8Array<ArrayBuffer> {
  const relleno = "=".repeat((4 - (base64.length % 4)) % 4);
  const normal = (base64 + relleno).replace(/-/g, "+").replace(/_/g, "/");
  const binario = atob(normal);
  const bytes = new Uint8Array(new ArrayBuffer(binario.length));
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
}

export async function registrarTrabajador(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/portal" });
  } catch {
    return null;
  }
}

export async function activar(): Promise<EstadoAvisos> {
  if (!soporta()) return esIosSinInstalar() ? "requiere-instalar" : "no-soportado";

  const permiso = await Notification.requestPermission();
  if (permiso !== "granted") return permiso === "denied" ? "bloqueados" : "sin-activar";

  const registro = (await registrarTrabajador()) ?? (await navigator.serviceWorker.ready);
  const { clave } = await api<{ clave: string }>("/portal/avisos/clave");

  const suscripcion =
    (await registro.pushManager.getSubscription()) ??
    (await registro.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: aBytes(clave),
    }));

  const datos = suscripcion.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } };
  await api("/portal/avisos", {
    metodo: "POST",
    cuerpo: { endpoint: datos.endpoint, claves: datos.keys },
  });

  return "activados";
}

export async function desactivar(): Promise<void> {
  const registro = await navigator.serviceWorker.getRegistration();
  const suscripcion = await registro?.pushManager.getSubscription();
  if (!suscripcion) return;

  await api("/portal/avisos", { metodo: "DELETE", cuerpo: { endpoint: suscripcion.endpoint } }).catch(() => undefined);
  await suscripcion.unsubscribe();
}

export async function enviarPrueba(): Promise<number> {
  const r = await api<{ enviados: number }>("/portal/avisos/prueba", { metodo: "POST" });
  return r.enviados;
}
